"use client";

import React from "react";
import { motion } from "framer-motion";
import Image from "next/image";

export interface AtrHeroBlockProps {
    tag?: string;
    title?: string;
    italicQuote?: string;
    description?: string;
    primaryBtnText?: string;
    primaryBtnLink?: string;
    secondaryBtnText?: string;
    secondaryBtnLink?: string;
    imageSrc?: string;
    systemStatus?: string;
    syncRate?: string;
    hudLabel?: string;
    hudParamsLeft?: string[];
    hudParamsRight?: string[];
}

export default function AtrHeroBlock({
    tag = "// Flight Telemetry & Physics",
    title = "ATR72-600",
    italicQuote = '"A high-fidelity Unity-powered digital twin translating flight data recorder telemetry logs into a fully synchronized 3D cockpit reconstruction."',
    description = "Our telemetry integration framework reads flight data recorder (FDR) parameter logs, plotting precise aerodynamics, landing gear states, control surface kinematics, and multi-system engine diagnostics with absolute real-time reliability.",
    primaryBtnText = "Explore Architecture",
    primaryBtnLink = "#telemetry-architecture",
    secondaryBtnText = "Monitored Parameters",
    secondaryBtnLink = "#monitored-parameters",
    imageSrc = "/images/atr72_simulation.png",
    systemStatus = "SYS: ACTIVE / FDR_FEED",
    syncRate = "SYNC RATE: 240Hz",
    hudLabel = "3D DIGITAL TWIN",
    hudParamsLeft = [
        "STATUS: FLIGHT_PLAYBACK",
        "ALT: 14,200 FT",
        "IAS: 228 KTS",
        "HDG: 274°"
    ],
    hudParamsRight = [
        "T_ENG_L: 76% • T_ENG_R: 76%",
        "NP_L: 82% • NP_R: 82%",
        "ITT_L: 712°C • ITT_R: 715°C"
    ]
}: AtrHeroBlockProps) {
    return (
        <section className="relative min-h-[90vh] pt-32 pb-16 px-6 md:px-12 lg:px-24 xl:px-32 flex items-center bg-[#0C0D0E] w-full" data-header-theme="light">
            {/* Tech grid texture background */}
            <div className="absolute inset-0 bg-[radial-gradient(#ffffff04_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0C0D0E]/50 via-transparent to-[#0C0D0E]" />

            <div className="container mx-auto z-10 w-full">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    
                    {/* Left Column: Typographic Details */}
                    <div className="lg:col-span-6 flex flex-col justify-center space-y-6">
                        <motion.div 
                            initial={{ opacity: 0, y: 30 }} 
                            animate={{ opacity: 1, y: 0 }} 
                            transition={{ duration: 0.8 }}
                            className="space-y-6"
                        >
                            {tag && (
                                <div className="inline-flex items-center gap-2 border-b border-white/10 pb-2">
                                    <span className="text-xs font-mono uppercase tracking-widest text-[#00FF66]">
                                        {tag}
                                    </span>
                                </div>
                            )}
                            
                            <h1 className="font-cormorant font-serif text-6xl md:text-8xl xl:text-9xl font-light tracking-tight text-white leading-[0.85]">
                                {title}
                            </h1>
                            
                            {italicQuote && (
                                <p className="font-cormorant font-serif text-xl md:text-2xl text-white/80 italic max-w-xl leading-relaxed pt-2">
                                    {italicQuote}
                                </p>
                            )}

                            {description && (
                                <p className="text-sm text-white/50 max-w-md leading-relaxed font-light font-sans">
                                    {description}
                                </p>
                            )}

                            <div className="pt-4 flex flex-wrap gap-4">
                                {primaryBtnText && (
                                    <a href={primaryBtnLink} className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-black hover:bg-[#00FF66] transition-all duration-300 font-mono text-[11px] uppercase tracking-wider font-semibold rounded-none cursor-pointer">
                                        {primaryBtnText}
                                    </a>
                                )}
                                {secondaryBtnText && (
                                    <a href={secondaryBtnLink} className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-white/20 text-white hover:border-[#00FF66] transition-all duration-300 font-mono text-[11px] uppercase tracking-wider font-semibold rounded-none">
                                        {secondaryBtnText}
                                    </a>
                                )}
                            </div>
                        </motion.div>
                    </div>

                    {/* Right Column: Multi-Function Display (MFD) Bezel Mockup */}
                    <div className="lg:col-span-6 w-full flex justify-center">
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.96 }} 
                            animate={{ opacity: 1, scale: 1 }} 
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="relative w-full max-w-xl"
                        >
                            {/* MFD Bezel Frame */}
                            <div className="relative bg-[#1A1C1E] p-4 md:p-6 border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.8)] rounded-xl group overflow-hidden w-full">
                                
                                {/* Screws on Bezel corners */}
                                <div className="absolute top-2 left-2 w-2 h-2 rounded-full bg-white/25 border border-black/30 shadow-inner flex items-center justify-center text-[5px] font-sans text-black select-none">+</div>
                                <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-white/25 border border-black/30 shadow-inner flex items-center justify-center text-[5px] font-sans text-black select-none">+</div>
                                <div className="absolute bottom-2 left-2 w-2 h-2 rounded-full bg-white/25 border border-black/30 shadow-inner flex items-center justify-center text-[5px] font-sans text-black select-none">+</div>
                                <div className="absolute bottom-2 right-2 w-2 h-2 rounded-full bg-white/25 border border-black/30 shadow-inner flex items-center justify-center text-[5px] font-sans text-black select-none">+</div>

                                {/* Screen Display Headers */}
                                <div className="flex items-center justify-between pb-3 text-[9px] font-mono text-white/40 tracking-wider">
                                    <div className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#00FF66] animate-pulse"></span>
                                        <span>{systemStatus}</span>
                                    </div>
                                    <div>{syncRate}</div>
                                    <div className="text-white/60">{hudLabel}</div>
                                </div>

                                {/* Simulator Screenshot Container */}
                                <div className="relative aspect-[16/8] bg-black overflow-hidden rounded-xs border border-white/[0.05]">
                                    {imageSrc && (
                                        <Image
                                            src={imageSrc}
                                            alt="ATR72-600 Flight Simulation Digital Twin render"
                                            fill
                                            className="object-cover opacity-90 group-hover:scale-[1.02] transition-transform duration-1000"
                                            priority
                                        />
                                    )}
                                    
                                    {/* HUD / CRT Glass Scanner lines */}
                                    <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] bg-[size:100%_4px,6px_100%] pointer-events-none opacity-40"></div>
                                    <div className="absolute inset-0 bg-radial-gradient from-transparent to-black/35 pointer-events-none"></div>

                                    {/* HUD Overlay Left */}
                                    {hudParamsLeft && hudParamsLeft.length > 0 && (
                                        <div className="absolute top-4 left-4 font-mono text-[9px] text-[#00FF66] space-y-1 bg-black/60 p-2 backdrop-blur-xs border border-[#00FF66]/20">
                                            {hudParamsLeft.map((p, i) => (
                                                <div key={i}>{p}</div>
                                            ))}
                                        </div>
                                    )}

                                    {/* HUD Overlay Right */}
                                    {hudParamsRight && hudParamsRight.length > 0 && (
                                        <div className="absolute bottom-4 right-4 font-mono text-[9px] text-[#00E5FF] space-y-1 bg-black/60 p-2 backdrop-blur-xs border border-[#00E5FF]/20">
                                            {hudParamsRight.map((p, i) => (
                                                <div key={i}>{p}</div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* MFD Physical Control Buttons at Bottom */}
                                <div className="flex justify-between items-center pt-3 mt-1 text-[8px] font-mono text-white/30 border-t border-white/[0.05]">
                                    <span>NAV</span>
                                    <span>PFD</span>
                                    <span>ENG DIAG</span>
                                    <span>FDR FEED</span>
                                    <span>MAP</span>
                                    <span>MENU</span>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                </div>
            </div>
        </section>
    );
}
